import { useAuth0 } from "@auth0/auth0-react";
import { useCallback } from "react";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import { request } from "types/request.ts";
import type { RequestConfig } from "types/requestConfig.ts";
import type { RequestErrorHandler } from "types/requestErrorHandler.ts";
import { useHomeUrl } from "./useHomeUrl.ts";
import { useNotification } from "./useNotification.ts";

export const useApi = () => {
  const { getAccessTokenSilently, getIdTokenClaims, loginWithRedirect } = useAuth0();
  const { customerId } = useParams();
  const { formatMessage } = useIntl();
  const { showErrorNotification } = useNotification();
  const navigate = useNavigate();
  const homeUrl = useHomeUrl();
  const currentUrl = window.location.origin;

  return useCallback(
    async <TReturnValue = any>(endpoint: string, requestConfig: RequestConfig<TReturnValue> = {}) => {
      const config: RequestConfig<TReturnValue> = {
        method: "GET",
        ...requestConfig,
      };
      const [_idToken, accessToken] = await Promise.all([getIdTokenClaims(), getAccessTokenSilently()]);
      const idToken = _idToken?.__raw;

      const getErrorMessage = (data: any) => {
        const errorMessage = typeof data?.error === "string" ? data.error : data?.errors?.message;
        return String(errorMessage || formatMessage({ id: "notification.unexpectedError" }));
      };

      const defaultErrorHandler: RequestErrorHandler = {
        401: () =>
          loginWithRedirect({
            authorizationParams: { redirectUri: currentUrl },
          }),
        403: () => navigate(`/403?homeUrl=${encodeURIComponent(homeUrl)}`),
        404: () => navigate(`/404?homeUrl=${encodeURIComponent(homeUrl)}`),
        default: (_: Response, data: unknown) =>
          config.defaultErrorMessage
            ? showErrorNotification(config.defaultErrorMessage)
            : showErrorNotification(formatMessage({ id: "notification.request.failed" }), getErrorMessage(data)),
      };

      const onErrorHandler = config.onError
        ? typeof config.onError === "function"
          ? config.onError
          : { ...defaultErrorHandler, ...config.onError }
        : defaultErrorHandler;

      const extendedConfig: RequestConfig<TReturnValue> = {
        ...config,
        onError: onErrorHandler,
        headers: {
          ...config.headers,
          "x-user-identity-token": accessToken,
          ...(idToken && { Authorization: `Bearer ${idToken}` }),
          ...(customerId && { "x-customer-id": customerId }),
        },
      };

      return request<TReturnValue>(endpoint, extendedConfig);
    },
    [
      getAccessTokenSilently,
      getIdTokenClaims,
      customerId,
      loginWithRedirect,
      formatMessage,
      navigate,
      showErrorNotification,
      currentUrl,
      homeUrl,
    ]
  );
};
